import moment from 'moment';
import i18n from '@/plugins/i18n';

export function formatDate(date: Date = new Date(), format: string = i18n.global.t('dateFormats.date')): string {
    return moment(date).format(format);
}

export function formatForDateTimeInput(date: string | Date | null): string {
    if (!date) {
        return '';
    }

    return moment(date).format('YYYY-MM-DDTHH:mm');
}

export function formatForDateInput(date: string | Date | null): string {
    if (!date) {
        return '';
    }

    return moment(date).format('YYYY-MM-DD');
}

export function parseFromDateTimeInput(value: string | Date | null): string | null {
    if (!value) {
        return null;
    }

    // Handle string values properly
    const stringValue = typeof value === 'string' ? value : value.toString();
    const parsed = moment(stringValue);

    return parsed.isValid() ? parsed.toISOString() : null;
}

export function parseFromDateInput(value: string | Date | null): string | null {
    if (!value) {
        return null;
    }

    // Handle string values properly and add time component for consistency
    const stringValue = typeof value === 'string' ? value : value.toString();
    const parsed = moment(stringValue);

    return parsed.isValid() ? parsed.toISOString() : null;
}

export function formatMessageTime(timestamp: string | Date): string {
    return moment(timestamp).format('HH:mm');
}

export function formatDateForDisplay(
    date: string | Date | null,
    inputType: 'date' | 'datetime-local' | 'time' = 'datetime-local',
): string {
    if (!date) {
        return '';
    }

    const momentDate = moment(date);
    if (!momentDate.isValid()) {
        return '';
    }

    switch (inputType) {
    case 'date':
        return momentDate.format('DD.MM.YYYY');
    case 'time':
        return momentDate.format('HH:mm');
    case 'datetime-local':
    default:
        return momentDate.format('DD.MM.YYYY HH:mm');
    }
}

export function formatRelativeTime(
    timestamp: string | Date,
    t: (key: string, params?: Record<string, any>) => string,
): string {
    const date = moment(timestamp);
    const now = moment();
    const diffInSeconds = now.diff(date, 'seconds');

    if (diffInSeconds < 60) {
        return t('chatbot.justNow');
    }

    const diffInMinutes = now.diff(date, 'minutes');
    if (diffInMinutes < 60) {
        return t('chatbot.minutesAgo', { minutes: diffInMinutes });
    }

    const diffInHours = now.diff(date, 'hours');
    if (diffInHours < 24) {
        return t('chatbot.hoursAgo', { hours: diffInHours });
    }

    const diffInDays = now.diff(date, 'days');
    if (diffInDays === 1) {
        return t('chatbot.yesterday');
    }

    if (diffInDays < 7) {
        return t('chatbot.daysAgo', { days: diffInDays });
    }

    return date.format('L'); // Localized date format
}

/** IANA name of the browser's timezone, for endpoints that split data into local days. */
export function viewerTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
